import React, { useState } from 'react'
import { createStyles, makeStyles } from 'transfix-ui/core'
import { Table, TableBody, TableCell, TableHead, TableRow, Button, Spinner } from 'transfix-ui'
import { EdiRoutingTableRow } from './EdiRoutingTableRow'
import { EdiMessageTypeForm } from './forms/EdiMessageTypeForm'
import { TEdiMessageTypeSchema } from './forms/EdiMessageTypeForm/EdiMessageTypeSchema'
import { useEdiMessageType } from './hooks/useEdiMessageType'
import { useEdiMessageTypeMutations } from './hooks/useEdiMessageTypeMutations'

interface IEdiRoutingTableProps {
  connectionConfigId: string
  connectionConfigType: 'InboundConnectionConfig' | 'OutboundConnectionConfig'
}

const useStyles = makeStyles(() =>
  createStyles({
    routingRules: {
      padding: '5px',
    },
  })
)

export const EdiRoutingTable = ({
  connectionConfigId,
  connectionConfigType,
}: IEdiRoutingTableProps) => {
  const [showAddForm, setShowAddForm] = useState(false)
  const classes = useStyles()
  const connectionConfigIds = [connectionConfigId]
  const { ediMessageTypes, loading: ediMessageTypesLoading } = useEdiMessageType({
    connectionConfigType,
    connectionConfigIds,
  })

  let handlerLabel = ''
  if (connectionConfigType === 'InboundConnectionConfig') {
    handlerLabel = 'Parser'
  } else if (connectionConfigType === 'OutboundConnectionConfig') {
    handlerLabel = 'Builder'
  }

  const showAddFormHandler = () => setShowAddForm(true)
  const cancelAddFormHandler = () => setShowAddForm(false)

  const { persistEdiMessageType } = useEdiMessageTypeMutations()
  const submitSuccessHandler = (formParams: TEdiMessageTypeSchema) => {
    persistEdiMessageType(formParams)
    setShowAddForm(false)
  }

  return (
    <>
      <div className={classes.routingRules}>
        {connectionConfigType === 'InboundConnectionConfig' && (
          <>
            <h5>Inbound Routing Rules</h5>
            <ul>
              <li>Only one routing rule should be defined for each transaction set + subtype.</li>
              <li>A self-serve parser takes precedence over classic parser if both are set.</li>
              <li>A self-serve parser will only be used if it has a live version.</li>
              <li>
                Leaving parser unspecified for a transaction set means that inbound files will be
                routed based on rules in backend code. This may not always be set up, so verify with
                an engineer that a mapping exists in the backend if needed.
              </li>
            </ul>
          </>
        )}
        {connectionConfigType === 'OutboundConnectionConfig' && (
          <>
            <h5>Outbound Routing Rules</h5>
            <ul>
              <li>Only one routing rule should be defined for each transaction set + subtype.</li>
              <li>A self-serve builder takes precedence over classic builder if both are set.</li>
              <li>
                A self-serve builder will only be used if the builder itself is live AND the builder
                has a live version.
              </li>
              <li>
                Leaving builder unspecified for a transaction set means that outbound files will be
                routed based on rules in backend code. This may not always be set up, so verify with
                an engineer that a mapping exists in the backend if needed.
              </li>
            </ul>
          </>
        )}
      </div>
      <hr />
      <div>
        {!showAddForm && <Button onClick={showAddFormHandler}>Add</Button>}
        {showAddForm && (
          <EdiMessageTypeForm
            ediMessageType={null}
            connectionConfigType={connectionConfigType}
            connectionConfigId={connectionConfigId}
            onSubmitSuccess={submitSuccessHandler}
            onCancel={cancelAddFormHandler}
          />
        )}
      </div>
      <hr />
      <Table>
        <TableHead>
          <TableRow tableCellType='th'>
            <TableCell>Transaction Set</TableCell>
            <TableCell>{handlerLabel}</TableCell>
            <TableCell> </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ediMessageTypesLoading && (
            <TableRow>
              <TableCell colSpan={3}>
                <Spinner />
              </TableCell>
            </TableRow>
          )}
          {ediMessageTypes.map(ediMessageType => (
            <EdiRoutingTableRow
              key={ediMessageType.id}
              connectionConfigType={connectionConfigType}
              connectionConfigId={connectionConfigId}
              ediMessageType={ediMessageType}
              onPersistEdiMessageType={persistEdiMessageType}
            />
          ))}
        </TableBody>
      </Table>
    </>
  )
}
